import type { ReactNode } from "react";

/**
 * Section card chrome, matched to `prototype/purchases/bill-new.html` `.section` /
 * `.section-head` / `.section-body`:
 *
 *   - rounded border + paper bg + subtle shadow
 *   - head: 14px/18px padding-top, 10px/18px padding-bottom, 1px border-b
 *   - step badge: 20×20 rounded-full, mono numerals, bg-bg / text-muted
 *   - body: 16px/18px padding all around
 *
 * The `action` slot lands at the head's right edge per the prototype's
 * `.section-head .right` rule.
 */
interface SectionCardProps {
  step?: string | undefined;
  title: string;
  description?: string | undefined;
  action?: ReactNode | undefined;
  children: ReactNode;
}

export function SectionCard({ step, title, description, action, children }: SectionCardProps) {
  return (
    <section className="overflow-hidden rounded-md border border-line bg-paper shadow-sm">
      <header className="flex items-start justify-between gap-3 border-b border-line px-[18px] pb-2.5 pt-3.5">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-ink">
            {step ? (
              <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-bg font-mono text-xs font-bold text-muted">
                {step}
              </span>
            ) : null}
            {title}
          </h2>
          {description ? <p className="ml-7 mt-0.5 text-xs text-muted">{description}</p> : null}
        </div>
        {action ? <div className="flex shrink-0 items-center gap-1.5">{action}</div> : null}
      </header>
      <div className="px-[18px] py-4">{children}</div>
    </section>
  );
}

interface FieldProps {
  id?: string | undefined;
  label: string;
  required?: boolean | undefined;
  help?: string | undefined;
  error?: string | undefined;
  children: ReactNode;
}

export function Field({ id, label, required = false, help, error, children }: FieldProps) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <label htmlFor={id} className="text-xs font-semibold text-ink-2">
        {label}
        {required ? <span className="ml-0.5 text-bad">*</span> : null}
      </label>
      {children}
      {error ? (
        <p className="text-xs font-medium text-bad">{error}</p>
      ) : help ? (
        <p className="text-xs text-muted">{help}</p>
      ) : null}
    </div>
  );
}
